/**
 * Secret redaction.
 *
 * Runs in the browser (instant preview) and again in the Worker (authoritative)
 * before anything is persisted or sent to a model provider.
 *
 * Redaction is best-effort by design. The UI always shows a review warning and
 * the generated reports repeat the caveat - we never claim that a paste is safe
 * just because it passed through this module.
 */

import type { RedactionHit, RedactionPlaceholder, SanitizationReport } from './types';

export const REDACTION_WARNING =
  'Redaction is best-effort and cannot be guaranteed. Review the sanitized text below and remove anything sensitive before saving or sharing.';

/** Field names whose *values* are always replaced, regardless of content. */
export const DEFAULT_SENSITIVE_FIELDS: readonly string[] = [
  'authorization',
  'auth',
  'authentication',
  'proxy-authorization',
  'x-api-key',
  'api_key',
  'api-key',
  'apikey',
  'x-auth-token',
  'x-access-token',
  'access_token',
  'refresh_token',
  'id_token',
  'auth_token',
  'token',
  'session_token',
  'session_id',
  'sessionid',
  'cookie',
  'set-cookie',
  'password',
  'passwd',
  'pwd',
  'pass',
  'secret',
  'client_secret',
  'clientsecret',
  'app_secret',
  'webhook_secret',
  'signing_secret',
  'signing_key',
  'private_key',
  'privatekey',
  'secret_key',
  'secretkey',
  'credential',
  'credentials',
  'aws_secret_access_key',
  'database_url',
  'db_url',
  'connection_string',
  'dsn',
  'csrf',
  'csrf_token',
  'xsrf',
  'otp',
  'pin',
  'cvv',
  'cvc',
  'card_number',
  'cardnumber',
  'ssn',
];

const SENSITIVE_FIELD_SET = new Set(DEFAULT_SENSITIVE_FIELDS.map((f) => f.toLowerCase()));

interface PatternRule {
  id: string;
  category: string;
  placeholder: RedactionPlaceholder;
  regex: RegExp;
}

/**
 * Ordered textual rules. Authorisation headers come first so that a Bearer
 * token inside a header is labelled as such instead of being caught by the
 * generic JWT rule.
 */
const TEXT_RULES: readonly PatternRule[] = [
  {
    id: 'authorization-header',
    category: 'Authorization header',
    placeholder: '[REDACTED_AUTHORIZATION]',
    regex: /((?:^|[\r\n"' ,;])(?:proxy-)?authorization\s*[:=]\s*)([^\r\n"']+)/gi,
  },
  {
    id: 'cookie-header',
    category: 'Cookie header',
    placeholder: '[REDACTED_COOKIE]',
    regex: /((?:^|[\r\n"' ,;])cookie\s*[:=]\s*)([^\r\n"']+)/gi,
  },
  {
    id: 'set-cookie-header',
    category: 'Set-Cookie header',
    placeholder: '[REDACTED_SET_COOKIE]',
    regex: /((?:^|[\r\n"' ,;])set-cookie\s*[:=]\s*)([^\r\n"']+)/gi,
  },
  {
    id: 'bearer-token',
    category: 'Bearer token',
    placeholder: '[REDACTED_BEARER_TOKEN]',
    regex: /\bbearer\s+([A-Za-z0-9\-._~+/=]{8,})/gi,
  },
  {
    id: 'basic-credentials',
    category: 'Basic auth credentials',
    placeholder: '[REDACTED_BASIC_CREDENTIALS]',
    regex: /\bbasic\s+([A-Za-z0-9+/]{8,}={0,2})/gi,
  },
  {
    id: 'private-key-block',
    category: 'Private key material',
    placeholder: '[REDACTED_PRIVATE_KEY]',
    regex:
      /-----BEGIN(?: [A-Z0-9]+)* PRIVATE KEY-----[\s\S]*?-----END(?: [A-Z0-9]+)* PRIVATE KEY-----/g,
  },
  {
    id: 'connection-string',
    category: 'Database connection string',
    placeholder: '[REDACTED_CONNECTION_STRING]',
    regex:
      /\b(?:postgres(?:ql)?|mysql2?|mariadb|mongodb(?:\+srv)?|redis|rediss|amqp|amqps|mssql|clickhouse):\/\/[^\s"'<>]+/gi,
  },
  {
    id: 'jwt',
    category: 'JWT',
    placeholder: '[REDACTED_JWT]',
    regex: /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{4,}/g,
  },
  {
    id: 'google-oauth-token',
    category: 'OAuth token',
    placeholder: '[REDACTED_OAUTH_TOKEN]',
    regex: /\bya29\.[A-Za-z0-9_-]{10,}/g,
  },
  {
    id: 'oauth-code-param',
    category: 'OAuth token',
    placeholder: '[REDACTED_OAUTH_TOKEN]',
    regex:
      /((?:^|[?&\s"',])(?:access_token|refresh_token|id_token|code)\s*[=:]\s*)([A-Za-z0-9\-._~+/=]{8,})/gi,
  },
  {
    id: 'provider-prefixed-secret',
    category: 'Provider API key',
    placeholder: '[REDACTED_API_KEY]',
    regex:
      /\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]{8,}|\bwhsec_[A-Za-z0-9]{8,}|\bsk-[A-Za-z0-9_-]{16,}|\b(?:ghp|gho|ghu|ghs|ghr)_[A-Za-z0-9]{16,}|\bgithub_pat_[A-Za-z0-9_]{16,}|\bxox[a-z]-[A-Za-z0-9-]{10,}|\bAKIA[0-9A-Z]{12,}|\bASIA[0-9A-Z]{12,}|\bAIza[0-9A-Za-z_-]{20,}|\bglpat-[A-Za-z0-9_-]{16,}|\bshp(?:at|ca|pa|sa)_[A-Za-z0-9]{16,}|\bnpm_[A-Za-z0-9]{20,}|\bpypi-[A-Za-z0-9_-]{20,}|\bAC[a-f0-9]{32}\b/g,
  },
  {
    id: 'key-value-pair',
    category: 'Credential value',
    placeholder: '[REDACTED_SECRET]',
    regex:
      /((?:^|[\r\n{,\s"'])(?:api[_-]?key|apikey|secret|client[_-]?secret|password|passwd|token|access[_-]?token|refresh[_-]?token|private[_-]?key)\s*["']?\s*[:=]\s*["']?)([^\s"',}]{6,})/gi,
  },
  {
    id: 'credit-card',
    category: 'Payment card number',
    placeholder: '[REDACTED_CREDIT_CARD]',
    regex: /\b(?:\d[ -]?){12,18}\d\b/g,
  },
  {
    id: 'email',
    category: 'Email address',
    placeholder: '[REDACTED_EMAIL]',
    regex: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
  },
];

const PLACEHOLDER_VALUE_RE = /^\[REDACTED_[A-Z_]+\]$/;

function luhnValid(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let value = digits.charCodeAt(i) - 48;
    if (value < 0 || value > 9) return false;
    if (double) {
      value *= 2;
      if (value > 9) value -= 9;
    }
    sum += value;
    double = !double;
  }
  return sum % 10 === 0;
}

export interface RedactOptions {
  /** Extra field names treated as sensitive, in addition to the defaults. */
  customSensitiveFields?: readonly string[];
  /** Redact email addresses. Defaults to true. */
  redactEmails?: boolean;
  /** Skip connection-string redaction (rarely needed). */
  redactConnectionStrings?: boolean;
  /** Hard cap so a pathological paste cannot pin the CPU. */
  maxLength?: number;
}

export interface RedactResult {
  text: string;
  report: SanitizationReport;
}

const MAX_REDACT_LENGTH = 2_000_000;

function isSensitiveKey(key: string, custom: ReadonlySet<string>): boolean {
  const normalised = key.trim().toLowerCase();
  if (SENSITIVE_FIELD_SET.has(normalised)) return true;
  if (custom.has(normalised)) return true;
  // Strip common separators and compare again, e.g. x_api_key -> xapikey.
  const compact = normalised.replace(/[^a-z0-9]/g, '');
  if (compact.length <= 2) return false;
  for (const candidate of [...SENSITIVE_FIELD_SET, ...custom]) {
    if (candidate.replace(/[^a-z0-9]/g, '') === compact) return true;
  }
  return false;
}

function placeholderFor(key: string): RedactionPlaceholder {
  const k = key.toLowerCase();
  if (k.includes('authorization')) return '[REDACTED_AUTHORIZATION]';
  if (k.includes('cookie')) return '[REDACTED_COOKIE]';
  if (k.includes('password') || k.includes('passwd') || k === 'pwd') return '[REDACTED_PASSWORD]';
  if (k.includes('secret')) return '[REDACTED_CLIENT_SECRET]';
  if (k.includes('api_key') || k.includes('api-key') || k === 'apikey' || k.startsWith('x-api'))
    return '[REDACTED_API_KEY]';
  if (k.includes('card') || k === 'cvv' || k === 'cvc') return '[REDACTED_CREDIT_CARD]';
  if (k.includes('private_key') || k.includes('privatekey')) return '[REDACTED_PRIVATE_KEY]';
  if (k.includes('database') || k.includes('dsn') || k.includes('connection'))
    return '[REDACTED_CONNECTION_STRING]';
  if (k.includes('token')) return '[REDACTED_OAUTH_TOKEN]';
  return '[REDACTED_SECRET]';
}

interface InternalHit {
  placeholder: RedactionPlaceholder;
  category: string;
  count: number;
}

class HitCollector {
  private readonly hits = new Map<string, InternalHit>();

  add(placeholder: RedactionPlaceholder, category: string, count = 1): void {
    if (count <= 0) return;
    const key = `${placeholder}::${category}`;
    const existing = this.hits.get(key);
    if (existing) {
      existing.count += count;
    } else {
      this.hits.set(key, { placeholder, category, count });
    }
  }

  report(customSensitiveFields: string[], notes: string[]): SanitizationReport {
    const hits: RedactionHit[] = [...this.hits.values()].sort(
      (a, b) => b.count - a.count || a.placeholder.localeCompare(b.placeholder),
    );
    const totalRedactions = hits.reduce((sum, hit) => sum + hit.count, 0);
    return {
      redacted: totalRedactions > 0,
      totalRedactions,
      hits,
      customSensitiveFields: [...customSensitiveFields],
      warning: REDACTION_WARNING,
      notes,
    };
  }
}

/**
 * Structurally redact a parsed JSON document. This is the safest pass: it
 * replaces the *entire value* of a sensitive key, so partial secrets and
 * unusual formats are covered.
 */
function redactStructured(
  value: unknown,
  custom: ReadonlySet<string>,
  collector: HitCollector,
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => redactStructured(item, custom, collector));
  }
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      if (isSensitiveKey(key, custom)) {
        const placeholder = placeholderFor(key);
        collector.add(placeholder, `Value of ${key}`);
        out[key] = placeholder;
      } else {
        out[key] = redactStructured(child, custom, collector);
      }
    }
    return out;
  }
  return value;
}

/**
 * Redact a raw string. Structured documents are redacted key-first, then a
 * textual sweep catches anything embedded in headers, query strings or prose.
 */
export function redactSecrets(input: string, options: RedactOptions = {}): RedactResult {
  const customSensitiveFields = options.customSensitiveFields ?? [];
  const custom = new Set(customSensitiveFields.map((f) => f.trim().toLowerCase()).filter(Boolean));
  const redactEmails = options.redactEmails !== false;
  const redactConnectionStrings = options.redactConnectionStrings !== false;
  const collector = new HitCollector();
  const notes: string[] = [];

  if (input.length === 0) {
    return { text: '', report: collector.report([...customSensitiveFields], ['Input was empty.']) };
  }

  let working = input;
  const limit = options.maxLength ?? MAX_REDACT_LENGTH;
  if (working.length > limit) {
    working = working.slice(0, limit);
    notes.push('Input was truncated before redaction because it exceeded the redaction limit.');
  }

  // Pass 1 - structural redaction for JSON documents.
  const trimmed = working.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      const redacted = redactStructured(parsed, custom, collector);
      working = JSON.stringify(redacted, null, 2);
      notes.push('Structured JSON pass applied (sensitive keys replaced entirely).');
    } catch {
      notes.push('Input looked like JSON but did not parse; only the textual pass was applied.');
    }
  }

  // Pass 2 - textual sweep.
  for (const rule of TEXT_RULES) {
    if (rule.id === 'email' && !redactEmails) continue;
    if (rule.id === 'connection-string' && !redactConnectionStrings) continue;

    const regex = new RegExp(rule.regex.source, rule.regex.flags);
    let count = 0;
    working = working.replace(regex, (...args: unknown[]) => {
      const groups = args.slice(0, -2) as Array<string | undefined>;
      const matched = groups[0];
      if (matched === undefined) return '';

      // Never re-redact something that is already a placeholder: the structural
      // pass may have run first and the textual sweep must not overwrite it.
      for (let i = 1; i < groups.length; i += 1) {
        const group = groups[i];
        if (typeof group === 'string' && PLACEHOLDER_VALUE_RE.test(group.trim())) return matched;
      }

      if (rule.id === 'credit-card') {
        const digits = matched.replace(/\D/g, '');
        if (digits.length < 13 || digits.length > 19 || !luhnValid(digits)) {
          return matched;
        }
      }

      count += 1;
      const prefix = groups[1];
      if (typeof prefix === 'string' && prefix.length > 0) {
        return `${prefix}${rule.placeholder}`;
      }
      return rule.placeholder;
    });

    if (count > 0) collector.add(rule.placeholder, rule.category, count);
  }

  if (notes.length === 0) {
    notes.push('Textual sweep applied; no structural pass was required.');
  }

  return {
    text: working,
    report: collector.report([...customSensitiveFields], notes),
  };
}

/**
 * Last-line-of-defence check: does this string still look like it holds a
 * high-entropy secret? Used before persisting or logging.
 */
export function looksUnredacted(text: string): boolean {
  const probes: RegExp[] = [
    /\bbearer\s+[A-Za-z0-9\-._~+/=]{16,}/i,
    /\bsk-(?:live|test)?[A-Za-z0-9_-]{16,}/,
    /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{4,}/,
    /-----BEGIN(?: [A-Z0-9]+)* PRIVATE KEY-----/,
    /\b(?:postgres(?:ql)?|mysql|mongodb(?:\+srv)?|redis):\/\/[^\s"']*:[^\s"'@]+@/i,
  ];
  return probes.some((re) => re.test(text));
}

/**
 * Normalise a user-supplied list of sensitive field names. Bounded so a caller
 * cannot flood the redaction pass.
 */
export function normaliseCustomFields(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const out: string[] = [];
  for (const item of input) {
    if (typeof item !== 'string') continue;
    const trimmed = item.trim();
    if (trimmed.length === 0 || trimmed.length > 64) continue;
    if (!/^[A-Za-z0-9_.\-\s]+$/.test(trimmed)) continue;
    out.push(trimmed);
    if (out.length >= 50) break;
  }
  return [...new Set(out)];
}
